<script setup lang="ts">
import { computed } from 'vue';

import IncomingPropertyReceiveRentalIncome from '@/components/application/incoming-property-receive-rental-income.vue';
import IncomingPropertyExpectedRentalIncome from '@/components/application/incoming-property-expected-rental-income.vue';
import IncomingPropertyTransferFeeCovered from '@/components/application/incoming-property-transfer-fee-covered.vue';
import IncomingPropertyIsNewBuild from '@/components/application/incoming-property-is-new-build.vue';
import IncomingPropertyContractExchangedBeforeMay2026 from '@/components/application/incoming-property-contract-exchanged-before-may-2026.vue';
import { stampDutyStatusOptions, StampDutyStatus, PropertyPurpose } from '@/helpers/const';
import { useNegativeGearingFy27Asset } from '@/helpers/useNegativeGearingFy27Asset';

import type { DepositPaidType } from '@/api/enums';
import type { PropertyPurposeType } from '@/types/api';

const props = defineProps<{
  id: string;
  contractForSale?: boolean;
  receiveRentalIncome?: boolean;
  propertyPurpose?: PropertyPurposeType;
  isNewBuild?: boolean;
}>();

const { showNewBuild, showContractExchange } = useNegativeGearingFy27Asset(
  computed(() => props.propertyPurpose),
  computed(() => props.isNewBuild),
);

const showContractExchangeQuestion = computed(() => showContractExchange.value && props.contractForSale === true);

const depositTypeOptions: Record<DepositPaidType, string> = {
  ['cash_payment']: 'Cash payment',
  ['deposit_bond']: 'Deposit bond',
};

// note: "already_paid" is only valid if contract for sale exists
const validStampDutyPaymentOptions = computed(() =>
  Object.fromEntries(
    Object.entries(stampDutyStatusOptions).filter(
      ([key]) => props.contractForSale || key !== StampDutyStatus.ALREADY_PAID,
    ),
  ),
);

const showRentalIncomeQuestion = computed(() => props.propertyPurpose === PropertyPurpose.INVESTMENT_PROPERTY);
</script>

<template>
  <div class="mt-6">
    <IncomingPropertyReceiveRentalIncome
      v-if="showRentalIncomeQuestion"
      :id="id"
      label="Is this property expected to generate rental income?"
    />
    <IncomingPropertyExpectedRentalIncome v-if="receiveRentalIncome" :id="id" />
    <BxField v-slot="{ field }" name="contractForSale">
      <BxRadio
        v-bind="field"
        :id="`${id}-contract-of-sale`"
        :data-test-id="`${id}-contract-of-sale`"
        label="Do you have a contract for sale?"
        label-type="default"
        class="form-control"
        boolean
      />
    </BxField>
    <IncomingPropertyIsNewBuild v-if="showNewBuild" :id="id" />
    <IncomingPropertyContractExchangedBeforeMay2026 v-if="showContractExchangeQuestion" :id="id" />
    <BxField v-if="contractForSale !== undefined" v-slot="{ field }" name="settlementDate">
      <BxDatePicker
        v-bind="field"
        :id="`${id}-${contractForSale ? 'settlement-date' : 'estimated-purchase-date'}`"
        :data-test-id="`${id}-settlement-date-estimated-purchase-date`"
        :label="contractForSale ? 'Settlement Date' : 'Estimated Purchase Date'"
        label-type="floating"
        placeholder="DD/MM/YYYY"
        class="form-control"
        :min-date="new Date()"
      />
    </BxField>
    <BxField v-if="contractForSale !== undefined" v-slot="{ field }" name="purchaseAmount">
      <BxNumberInput
        v-bind="field"
        :id="`${id}-${contractForSale ? 'purchase-price' : 'estimated-purchase-price'}`"
        :data-test-id="`${id}-purchase-price-estimated-purchase-price`"
        :label="contractForSale ? 'Purchase Price' : 'Estimated Purchase Price'"
        label-type="floating"
        leading-symbol="$"
        class="form-control"
      />
    </BxField>
    <BxField v-if="contractForSale !== undefined" v-slot="{ field }" name="depositAmount">
      <BxNumberInput
        v-bind="field"
        :id="`${id}-${contractForSale ? 'deposit-amount' : 'estimated-deposit-amount'}`"
        :data-test-id="`${id}-deposit-amount-estimated-deposit-amount`"
        :label="contractForSale ? 'Deposit Amount' : 'Estimated Deposit Amount'"
        label-type="floating"
        leading-symbol="$"
        class="form-control"
      />
    </BxField>
    <BxField v-if="contractForSale !== undefined" v-slot="{ field }" name="depositType">
      <BxRadio
        v-bind="field"
        :id="`${id}-deposit-type`"
        :data-test-id="`${id}-deposit-type`"
        :options="depositTypeOptions"
        :label="contractForSale ? 'How was the deposit paid?' : 'How will the deposit be paid?'"
        label-type="default"
        class="form-control"
      />
    </BxField>
    <BxField v-if="contractForSale !== undefined" v-slot="{ field }" name="stampDutyStatus">
      <BxRadio
        v-bind="field"
        :id="`${id}-stamp-duty-paid`"
        :data-test-id="`${id}-stamp-duty-paid`"
        :options="validStampDutyPaymentOptions"
        label="How will stamp duty be paid?"
        label-type="default"
        class="form-control"
        block
      />
    </BxField>
    <IncomingPropertyTransferFeeCovered
      v-if="contractForSale !== undefined"
      :id="id"
      label="Is coverage of the transfer fee required?"
    />
  </div>
</template>
